/**
 * UpdateService - Simple auto-update system
 * 
 * Checks version.json periodically and automatically reloads when update is detected
 * if app is in background. Otherwise updates on next navigation or when app goes to background.
 */

interface VersionData {
  version: string;
  buildDate?: string;
  features?: string[];
  updateType?: 'critical' | 'feature' | 'bugfix';
  changelog?: string;
}

// Preserve the native history methods on first evaluation so we can restore
// them if a previous version of UpdateService monkey-patched them.
const _nativePushState = (window as any).__nativePushState ??
  ((window as any).__nativePushState = history.pushState.bind(history));
const _nativeReplaceState = (window as any).__nativeReplaceState ??
  ((window as any).__nativeReplaceState = history.replaceState.bind(history));

// Immediately restore native history methods if they've been monkey-patched.
// This runs every time the module is evaluated (including HMR hot-reload),
// so it fixes the patched methods even before initialize() is called.
if (history.pushState !== _nativePushState) {
  console.log('🔧 UpdateService (module load): Restoring native history.pushState');
  history.pushState = _nativePushState;
}
if (history.replaceState !== _nativeReplaceState) {
  console.log('🔧 UpdateService (module load): Restoring native history.replaceState');
  history.replaceState = _nativeReplaceState;
}

class UpdateService {
  private checkInterval: NodeJS.Timeout | null = null;
  private currentVersion: string | null = null;
  private isInitialized = false;
  private isAppInBackground = false;
  private pendingUpdate = false;

  // Configuration
  private readonly CHECK_INTERVAL = 2 * 60 * 1000; // Check every 2 minutes
  private readonly VERSION_STORAGE_KEY = 'nexus-app-version';

  /**
   * Initialize the update service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      console.warn('UpdateService: Already initialized');
      return;
    }

    try {
      console.log('🔄 UpdateService: Initializing simple auto-update system...');

      // Restore native history methods in case a previous version patched them.
      // The old update logic monkey-patched pushState/replaceState to reload the
      // page on the next navigation, which broke React Router's client-side routing.
      this.restoreHistoryMethods();

      // Get current version from localStorage or version.json
      await this.loadCurrentVersion();

      // Setup visibility change listener to detect when app goes to background
      this.setupVisibilityListener();

      // Start periodic checking
      this.startPeriodicChecking();

      this.isInitialized = true;
      console.log('✅ UpdateService: Initialized successfully');
    } catch (error) {
      console.error('❌ UpdateService: Initialization failed:', error);
    }
  }

  /**
   * Restore native history.pushState / history.replaceState if they were
   * previously monkey-patched by an older version of the update service.
   */
  private restoreHistoryMethods(): void {
    if (history.pushState !== _nativePushState) {
      console.log('🔧 UpdateService: Restoring native history.pushState');
      history.pushState = _nativePushState;
    }
    if (history.replaceState !== _nativeReplaceState) {
      console.log('🔧 UpdateService: Restoring native history.replaceState');
      history.replaceState = _nativeReplaceState;
    }
  }

  /**
   * Setup visibility API listener to detect when app is in background
   */
  private setupVisibilityListener(): void {
    this.isAppInBackground = document.hidden;

    document.addEventListener('visibilitychange', () => {
      this.isAppInBackground = document.hidden;
      
      if (document.hidden) {
        console.log('📱 App went to background');
        // If there's a pending update, apply it now while user isn't looking
        if (this.pendingUpdate) {
          console.log('🔄 Applying pending update while app is in background...');
          this.clearCachesAndReload();
          return;
        }
        this.checkForUpdates();
      } else {
        console.log('📱 App came to foreground');
        this.checkForUpdates();
      }
    });
  }


  /**
   * Load current version from localStorage or fetch from version.json
   */
  private async loadCurrentVersion(): Promise<void> {
    // Try to get from localStorage first
    const storedVersion = localStorage.getItem(this.VERSION_STORAGE_KEY);
    if (storedVersion) {
      this.currentVersion = storedVersion;
      console.log('📦 UpdateService: Current version from storage:', this.currentVersion);
      return;
    }

    // If not in storage, fetch from version.json
    try {
      const versionData = await this.fetchVersionData();
      if (versionData) {
        this.currentVersion = versionData.version;
        localStorage.setItem(this.VERSION_STORAGE_KEY, versionData.version);
        console.log('📦 UpdateService: Current version from server:', this.currentVersion);
      }
    } catch (error) {
      console.warn('⚠️ UpdateService: Could not fetch initial version:', error);
    }
  }

  /**
   * Start periodic version checking
   */
  private startPeriodicChecking(): void {
    // Check immediately on initialization
    this.checkForUpdates();

    // Then check periodically
    this.checkInterval = setInterval(() => {
      this.checkForUpdates();
    }, this.CHECK_INTERVAL);

    console.log(`🔄 UpdateService: Started periodic checking (every ${this.CHECK_INTERVAL / 1000 / 60} minutes)`);
  }

  /**
   * Check for updates by comparing version.json with stored version
   * Auto-reloads if update detected and app is in background
   */
  async checkForUpdates(): Promise<boolean> {
    try {
      const versionData = await this.fetchVersionData();
      if (!versionData) {
        return false;
      }

      const newVersion = versionData.version;

      // If we don't have a current version yet, store it and return
      if (!this.currentVersion) {
        this.currentVersion = newVersion;
        localStorage.setItem(this.VERSION_STORAGE_KEY, newVersion);
        console.log('📦 UpdateService: Initial version stored:', newVersion);
        return false;
      }

      // Check if version changed
      if (this.isNewVersion(newVersion, this.currentVersion)) {
        console.log(`🔄 UpdateService: New version detected: ${this.currentVersion} → ${newVersion}`);
        
        // Update stored version
        this.currentVersion = newVersion;
        localStorage.setItem(this.VERSION_STORAGE_KEY, newVersion);

        // If app is in background, auto-reload immediately
        if (this.isAppInBackground) {
          console.log('📱 App is in background - auto-reloading to apply update...');
          await this.clearCachesAndReload();
          return true;
        } else {
          // App is active — flag the pending update but DON'T reload during navigation.
          // Reloading on pushState breaks client-side routing, making the entire
          // SPA refresh every time the user switches pages.
          // Instead, only auto-reload when the app next goes to background.
          console.log('📱 App is active - will reload when app goes to background');
          this.pendingUpdate = true;
          return true;
        }
      }

      return false;
    } catch (error) {
      console.warn('⚠️ UpdateService: Error checking for updates:', error);
      return false;
    }
  }

  /**
   * Clear caches and reload the page
   * CRITICAL: Do NOT unregister service worker - this kills push notification subscriptions
   * Service worker will update naturally, preserving push subscriptions
   */
  private async clearCachesAndReload(): Promise<void> {
    try {
      // Clear service worker caches (but NOT the service worker itself)
      // This allows service worker to update naturally while preserving push subscriptions
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        // Only clear app caches, not service worker registration
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        console.log('🗑️ UpdateService: Caches cleared (service worker preserved for push notifications)');
      }

      // CRITICAL: Do NOT unregister service worker
      // Unregistering kills all push notification subscriptions
      // Service worker will update automatically on next page load, preserving subscriptions
      // The pushsubscriptionchange event will handle subscription updates
      console.log('✅ UpdateService: Service worker preserved (push subscriptions maintained)');
    } catch (error) {
      console.warn('⚠️ UpdateService: Error clearing caches:', error);
    }

    // Reload the page - service worker will update naturally
    window.location.reload();
  }

  /**
   * Fetch version data from version.json
   */
  private async fetchVersionData(): Promise<VersionData | null> {
    try {
      const response = await fetch('/version.json', {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: VersionData = await response.json();
      return data;
    } catch (error) {
      console.warn('⚠️ UpdateService: Failed to fetch version.json:', error);
      return null;
    }
  }

  /**
   * Compare version strings to determine if new version is available
   * Supports semantic versioning (e.g., "2025.1.21" vs "2025.1.22")
   */
  private isNewVersion(newVersion: string, currentVersion: string): boolean {
    // Simple string comparison for now
    // Can be enhanced to support semantic versioning if needed
    return newVersion !== currentVersion;
  }

  /**
   * Get current version
   */
  getCurrentVersion(): string | null {
    return this.currentVersion;
  }

  /**
   * Get the latest version from server
   */
  async getLatestVersion(): Promise<string | null> {
    try {
      const versionData = await this.fetchVersionData();
      return versionData?.version || null;
    } catch (error) {
      console.warn('⚠️ UpdateService: Error fetching latest version:', error);
      return null;
    }
  }

  /**
   * Manually trigger update check (useful for testing)
   */
  async manualCheck(): Promise<boolean> {
    console.log('🔄 UpdateService: Manual update check triggered');
    return await this.checkForUpdates();
  }

  /**
   * Cleanup - stop periodic checking
   */
  destroy(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    this.isInitialized = false;
    console.log('🧹 UpdateService: Destroyed');
  }
}

// Export singleton instance
export const updateService = new UpdateService();
